import React, { useEffect, useRef } from "react";
import type { CSSProperties } from "react";
import { accent1 as accent, useSlide1, Seg } from "./slide1.data";
import { FONT_BODY, FONT_DISPLAY, FONT_MONO } from "theme/fonts";

/* Portrait/mobile layout for Slide 1 — flowing, scrollable, no canvas.
   Reads the same content as the desktop slide from slide1.data.ts. */

const font = FONT_BODY;
const mono = FONT_MONO;

const lbl: CSSProperties = { fontFamily: mono, fontSize: 10, letterSpacing: 2.5, color: accent, textTransform: "uppercase", margin: "0 0 6px" };
const txt: CSSProperties = { fontFamily: FONT_BODY, fontStyle: "normal", fontSize: 15, lineHeight: 1.65, color: "#cde0f2", margin: 0 };

const renderSegs = (segs: Seg[]) =>
  segs.map((s, i) =>
    s.b ? <b key={i} style={{ color: "#dceeff", fontWeight: 700 }}>{s.t}</b>
    : s.em ? <em key={i}>{s.t}</em>
    : <React.Fragment key={i}>{s.t}</React.Fragment>
  );

const HTLDSlide1Mobile: React.FC = () => {
  const { meta: slide1Meta, whatItIs, approach, notList, stats } = useSlide1();
  const fontRef = useRef<HTMLLinkElement | null>(null);
  useEffect(() => {
    if (!document.querySelector('link[href*="Space+Grotesk"]')) {
      const l = document.createElement("link"); l.rel = "stylesheet";
      l.href = "https://fonts.googleapis.com/css2?family=Merriweather:ital,wght@0,300;0,400;0,700;1,300;1,400;1,700&display=swap";
      document.head.appendChild(l); fontRef.current = l;
    }
  }, []);

  return (
    <div style={{ background: "#040f18", color: "#d0e8f8", fontFamily: font, padding: "22px 18px 40px", boxSizing: "border-box", height: "100%", width: "100%", overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
      {/* breadcrumb */}
      <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: 2, color: "#5888b0", textTransform: "uppercase", marginBottom: 8 }}>
        {slide1Meta.breadcrumbPre} · <span style={{ color: accent }}>{slide1Meta.breadcrumbAccent}</span>
      </div>

      {/* title */}
      <h1 style={{ fontFamily: FONT_DISPLAY, fontStyle: "normal", fontWeight: 600, fontSize: 27, lineHeight: 1.18, margin: "0 0 4px", color: "#e8f4ff" }}>
        {slide1Meta.titlePlain} <span style={{ color: accent }}>{slide1Meta.titleAccent}</span>
      </h1>
      <p style={{ fontFamily: FONT_BODY, fontStyle: "normal", fontWeight: 300, fontSize: 14, color: "#7aa8cc", margin: "0 0 20px" }}>
        {slide1Meta.subtitle}
      </p>

      {/* what it is */}
      <div style={{ marginBottom: 18 }}>
        <div style={lbl}>{slide1Meta.whatItIsLabel}</div>
        <p style={txt}>{renderSegs(whatItIs)}</p>
      </div>

      {/* unique approach */}
      <div style={{
        background: "rgba(55,138,221,0.07)", border: "1px solid rgba(55,138,221,0.20)",
        borderRadius: 10, padding: "13px 15px", marginBottom: 18,
      }}>
        <div style={lbl}>{slide1Meta.approachLabel}</div>
        <p style={{ ...txt, color: "#d0e8ff" }}>{renderSegs(approach)}</p>
      </div>

      {/* what it is not */}
      <div style={{ marginBottom: 18 }}>
        <div style={lbl}>{slide1Meta.notLabel}</div>
        {notList.map((item, i) => (
          <div key={i} style={{
            display: "flex", gap: 10, alignItems: "flex-start",
            background: "rgba(55,138,221,0.03)", border: "1px solid rgba(55,138,221,0.08)",
            borderRadius: 8, padding: "9px 12px", marginBottom: 8,
          }}>
            <span style={{ color: "rgba(220,90,90,0.85)", flexShrink: 0, fontSize: 13, marginTop: 1 }}>✕</span>
            <span style={{ fontFamily: FONT_BODY, fontStyle: "normal", fontWeight: 300, fontSize: 14, lineHeight: 1.5, color: "#9bbdd6" }}>{item}</span>
          </div>
        ))}
      </div>

      {/* stats (2×2) */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        {stats.map((f, i) => (
          <div key={i} style={{
            background: "rgba(55,138,221,0.05)", border: "1px solid rgba(55,138,221,0.12)",
            borderRadius: 8, padding: "10px 8px", textAlign: "center",
          }}>
            <div style={{ fontFamily: mono, fontSize: 8, letterSpacing: 2, color: "rgba(55,138,221,0.6)", marginBottom: 4 }}>
              {f.label.toUpperCase()}
            </div>
            <div style={{ fontFamily: FONT_BODY, fontStyle: "normal", fontWeight: 700, fontSize: 14, color: "#c8dff5" }}>
              {f.value}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default HTLDSlide1Mobile;